import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Ban, Download, LayoutDashboard, LogOut, MoreHorizontal, Plus, RefreshCw, Search, ShieldAlert, Trash2, UserPlus, Users, UsersRound } from 'lucide-react';
import {
  GROUP_COLORS,
  PROTOCOL_CATEGORIES,
  PROTOCOL_CATEGORY_LABELS,
  ROLE_LABELS,
  TEAM_ROLE_LABELS,
  teamRoleAtLeast,
  type GroupBoards,
  type GroupColor,
  type GroupImpact,
  type MyTeamInvitation,
  type OwnerlessBoard,
  type ProtocolCategory,
  type ProtocolEntry,
  type TeamExternal,
  type TeamGroup,
  type TeamMember,
  type TeamRole,
} from '@kaba/shared';
import { api, ApiError } from '../lib/api';
import { useMe } from '../lib/auth';
import { useBoards } from '../lib/board';
import { navigate, setQuery, useLocation } from '../lib/router';
import { refreshTeam, useTeam, useTeamProtocol, useTeams, type ProtocolFilter, type TeamDetail } from '../lib/teams';
import { toast } from '../lib/toast';
import { GROUP_DOT } from '../lib/groupColors';
import { refreshStructure } from '../lib/structure';
import { queryClient } from '../lib/board';
import { Avatar, Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownTrigger, IconButton, Sheet } from './ui/ui';

const field = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-10 sm:text-sm';

export { GROUP_DOT };
const GROUP_COLOR_NAMES: Record<GroupColor, string> = { gray: 'Grau', blue: 'Blau', green: 'Grün', yellow: 'Gelb', orange: 'Orange', red: 'Rot', purple: 'Lila', pink: 'Pink' };

function Page({ title, back, children, actions }: { title: string; back: () => void; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-full max-w-3xl flex-col px-4 pb-16">
      <header className="pt-safe flex items-center gap-2 py-3">
        <IconButton label="Zurück" onClick={back}>
          <ArrowLeft size={20} />
        </IconButton>
        <h1 className="min-w-0 flex-1 truncate text-xl font-bold">{title}</h1>
        {actions}
      </header>
      <main className="flex flex-col">{children}</main>
    </div>
  );
}

/** /teams – eigene Teams und neues Team */
export function TeamsPage() {
  const { data: teams, isLoading } = useTeams();
  const { data: me } = useMe();
  const [creating, setCreating] = useState(false);
  return (
    <Page
      title="Teams"
      back={() => navigate('/')}
      actions={
        !me?.isGuest && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus size={17} aria-hidden /> Neues Team
          </Button>
        )
      }
    >
      <p className="mb-4 text-sm text-[var(--ink-soft)]">
        Ein Team ist der gemeinsame Rahmen für Mitglieder und Gruppen. Die Aufnahme ins Team öffnet noch keine Boards – Boards werden immer ausdrücklich
        freigegeben.
      </p>
      <MyInvitations />
      {isLoading && <p className="text-sm text-[var(--ink-soft)]">Lade …</p>}
      {teams && teams.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-8 text-center text-[var(--ink-soft)]">Du gehörst noch zu keinem Team.</div>
      )}
      <ul className="flex flex-col gap-2">
        {teams?.map((t) => (
          <li key={t.id}>
            <a
              href={`/teams/${t.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/teams/${t.id}`);
              }}
              className="flex min-h-16 items-center gap-3 rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-4 hover:border-accent"
            >
              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-fg">
                <Users size={20} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{t.name}</span>
                <span className="text-xs text-[var(--ink-soft)]">
                  {t.memberCount === 1 ? '1 Mitglied' : `${t.memberCount} Mitglieder`} · du: {TEAM_ROLE_LABELS[t.myRole]}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
      <CreateTeamDialog open={creating} onClose={() => setCreating(false)} />
    </Page>
  );
}

/** Offene Teameinladungen an die eigene Adresse: jede einzeln annehmen oder ablehnen (F03) */
function MyInvitations() {
  const { data } = useQuery({ queryKey: ['my-team-invitations'], queryFn: () => api.get<MyTeamInvitation[]>('/api/me/team-invitations') });
  const [busy, setBusy] = useState<string | null>(null);
  if (!data?.length) return null;
  const act = async (inv: MyTeamInvitation, action: 'accept' | 'decline') => {
    setBusy(inv.id);
    try {
      await api.post(`/api/me/team-invitations/${inv.id}/${action}`, {});
      toast.info(action === 'accept' ? `Du bist jetzt im Team „${inv.teamName}“.` : 'Einladung abgelehnt.');
      if (action === 'accept') navigate(`/teams/${inv.teamId}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(null);
      await queryClient.invalidateQueries({ queryKey: ['my-team-invitations'] });
      await queryClient.invalidateQueries({ queryKey: ['teams'] });
      await queryClient.invalidateQueries({ queryKey: ['boards'] });
    }
  };
  return (
    <section aria-labelledby="my-inv-title" className="mb-5 flex flex-col gap-2">
      <h2 id="my-inv-title" className="text-sm font-semibold">
        Einladungen für dich
      </h2>
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-accent/40 bg-[var(--panel)]">
        {data.map((inv) => (
          <li key={inv.id} className="flex flex-wrap items-center gap-3 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-medium">Team „{inv.teamName}“</p>
              <p className="text-xs text-[var(--ink-soft)]">
                als {TEAM_ROLE_LABELS[inv.role]}
                {inv.inviterName ? ` · eingeladen von ${inv.inviterName}` : ''}
                {inv.expiresAt ? ` · gültig bis ${new Date(inv.expiresAt).toLocaleDateString('de-DE')}` : ''}
              </p>
              {inv.groups.length > 0 && <p className="mt-1 text-xs text-[var(--ink-soft)]">Mit dem Beitritt in: {inv.groups.join(', ')}</p>}
            </div>
            <div className="flex gap-2">
              <Button disabled={busy === inv.id} onClick={() => void act(inv, 'decline')}>
                Ablehnen
              </Button>
              <Button variant="primary" disabled={busy === inv.id} onClick={() => void act(inv, 'accept')}>
                Annehmen
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CreateTeamDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Neues Team" side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const t = await api.post<{ id: string }>('/api/teams', { name, description: description || null });
            await refreshTeam(t.id);
            onClose();
            navigate(`/teams/${t.id}`);
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name
          <input className={field} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="z. B. Geschäftsstelle" />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Beschreibung (optional)
          <input className={field} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <p className="text-xs text-[var(--ink-soft)]">Du wirst Besitzer des Teams.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            Anlegen
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

import { Headline, PersonName } from './PersonDialog';
import { useDashboards } from './dashboard/DashboardView';
import { BrandingSummary } from './branding/BrandingSummary';
import { LibraryBrowser } from './library/LibraryBrowser';
import { refreshLibrary, useLibrary } from '../lib/library';

const TABS = [
  ['mitglieder', 'Mitglieder'],
  ['gruppen', 'Gruppen'],
  ['bibliothek', 'Bibliothek'],
  ['allgemein', 'Allgemein'],
  ['erscheinungsbild', 'Erscheinungsbild'],
  ['verwaltung', 'Verwaltung'],
  ['protokoll', 'Protokoll'],
] as const;
type Tab = (typeof TABS)[number][0];

/** /teams/:id – Teameinstellungen an einem Ort (U01) */
export function TeamSettingsPage({ teamId }: { teamId: string }) {
  const { data, error } = useTeam(teamId);
  const { query } = useLocation();
  const raw = query.get('tab') as Tab | null;
  const admin = !!data && teamRoleAtLeast(data.team.myRole, 'admin');
  const owner = data?.team.myRole === 'owner';
  const tabs = TABS.filter(([k]) => (k !== 'protokoll' || admin) && (k !== 'verwaltung' || owner));
  const tab: Tab = raw && tabs.some(([k]) => k === raw) ? raw : 'mitglieder';
  if (error)
    return (
      <Page title="Team" back={() => navigate('/teams')}>
        <p className="text-sm">Dieses Team gibt es nicht oder du gehörst nicht (mehr) dazu.</p>
      </Page>
    );
  if (!data)
    return (
      <Page title="Team" back={() => navigate('/teams')}>
        <p className="text-sm text-[var(--ink-soft)]">Lade …</p>
      </Page>
    );
  return (
    <Page title={data.team.name} back={() => navigate('/teams')}>
      <p className="-mt-1 mb-3 text-sm text-[var(--ink-soft)]">
        Teameinstellungen · du bist {TEAM_ROLE_LABELS[data.team.myRole]}
        {data.team.description ? ` · ${data.team.description}` : ''}
      </p>
      <TeamDashboards teamId={teamId} canCreate={admin} />
      <div role="tablist" aria-label="Teameinstellungen" className="mb-4 flex gap-1 overflow-x-auto rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setQuery({ tab: k === 'mitglieder' ? null : k }, { replace: true })}
            className={cx('min-h-11 shrink-0 rounded-md px-3 text-sm sm:min-h-10', tab === k ? 'bg-[var(--panel)] font-semibold shadow-sm' : 'text-[var(--ink-soft)]')}
          >
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={tabs.find(([k]) => k === tab)?.[1]}>
        {tab === 'mitglieder' && <MembersTab data={data} />}
        {tab === 'gruppen' && <GroupsTab data={data} />}
        {tab === 'bibliothek' && <TeamLibraryTab teamId={teamId} admin={admin} />}
        {tab === 'allgemein' && <GeneralTab data={data} />}
        {tab === 'erscheinungsbild' && <BrandingSummary target={{ scope: 'team', teamId, name: data.team.name }} />}
        {tab === 'verwaltung' && owner && <ManagementTab data={data} />}
        {tab === 'protokoll' && admin && <ProtocolTab teamId={teamId} data={data} />}
      </div>
    </Page>
  );
}

/* ---------- Mitglieder ---------- */

/**
 * Team-Bibliothek (0.57): Dateien für alle Boards des Teams. Admins legen fest, wer ändern darf, und
 * geben Ordner der obersten Ebene über ⋯ für Boards frei.
 */
function TeamLibraryTab({ teamId, admin }: { teamId: string; admin: boolean }) {
  const { data } = useLibrary({ kind: 'team', teamId }, { folder: null, q: '', trash: false, type: 'all' });
  const [busy, setBusy] = useState(false);
  const edit = data?.teamEdit ?? 'admins';
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-[var(--ink-soft)]">
        Dateien, die das ganze Team braucht – Logos, Vorlagen, Satzung. Ordner der obersten Ebene gibst du über ⋯ für alle oder ausgewählte Boards des
        Teams frei; dort stehen sie unter „Dateien → Team“ und lassen sich an Karten hängen.
      </p>
      {admin && (
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">Ändern dürfen</span>
          <select
            className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2 sm:min-h-9"
            value={edit}
            disabled={busy || !data}
            onChange={async (e) => {
              const value = e.target.value;
              setBusy(true);
              try {
                await api.patch(`/api/teams/${teamId}/library/settings`, { edit: value });
                await refreshLibrary({ kind: 'team', teamId });
                toast.info(value === 'members' ? 'Alle Mitglieder dürfen die Team-Bibliothek ändern.' : 'Nur Admins ändern die Team-Bibliothek.');
              } catch (err) {
                toast.error((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <option value="admins">Nur Admins</option>
            <option value="members">Alle Mitglieder</option>
          </select>
          <span className="text-xs text-[var(--ink-soft)]">Lesen dürfen alle Mitglieder; freigeben nur Admins.</span>
        </label>
      )}
      <LibraryBrowser scope={{ kind: 'team', teamId }} />
    </div>
  );
}

function MembersTab({ data }: { data: TeamDetail }) {
  const { data: me } = useMe();
  const teamId = data.team.id;
  const my = data.team.myRole;
  const admin = teamRoleAtLeast(my, 'admin');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<TeamRole | ''>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [editGroups, setEditGroups] = useState<TeamMember | null>(null);
  const [inviting, setInviting] = useState(false);
  const [view, setView] = useState<'mitglieder' | 'externe'>('mitglieder');
  const [blocked, setBlocked] = useState<{ member: TeamMember; message: string; boards: { id: string; title: string }[] } | null>(null);
  const [emergency, setEmergency] = useState<TeamMember | null>(null);
  const groupById = useMemo(() => new Map(data.groups.map((g) => [g.id, g])), [data.groups]);
  const shown = data.members.filter(
    (m) =>
      (!q.trim() || `${m.name} ${m.email}`.toLowerCase().includes(q.trim().toLowerCase())) &&
      (!roleFilter || m.role === roleFilter) &&
      (!groupFilter || (groupFilter === 'ohne' ? m.groupIds.length === 0 : m.groupIds.includes(groupFilter))),
  );
  const filtered = !!(q.trim() || roleFilter || groupFilter);
  const run = async (fn: () => Promise<unknown>, ok?: string, member?: TeamMember) => {
    try {
      await fn();
      if (ok) toast.info(ok);
    } catch (err) {
      // Allein besessene Boards: erklären statt nur melden (L03)
      const details = err instanceof ApiError && err.status === 409 ? (err.data as { details?: { boards?: { id: string; title: string }[] } })?.details : undefined;
      if (member && details?.boards) setBlocked({ member, message: (err as Error).message, boards: details.boards });
      else toast.error((err as Error).message);
    } finally {
      await refreshTeam(teamId);
    }
  };
  const canEditRole = (m: TeamMember) => admin && m.id !== me?.id && (my === 'owner' || m.role === 'member');
  const roleOptions = (m: TeamMember): TeamRole[] => (my === 'owner' ? ['member', 'admin', 'owner'] : m.role === 'member' ? ['member', 'admin'] : [m.role]);

  if (view === 'externe')
    return (
      <div className="flex flex-col gap-4">
        <ViewSwitch view={view} onChange={setView} />
        <ExternalsList teamId={teamId} />
      </div>
    );

  return (
    <div className="flex flex-col gap-4">
      {admin && <ViewSwitch view={view} onChange={setView} />}
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Mitglieder suchen</span>
          <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[var(--ink-soft)]" aria-hidden />
          <input className={cx(field, 'pl-9')} placeholder="Mitglieder suchen …" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {admin && (
          <Button variant="primary" onClick={() => setInviting(true)}>
            <UserPlus size={17} aria-hidden /> Ins Team einladen
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="w-full sm:w-auto">
          <span className="sr-only">Nach Rolle filtern</span>
          <select className={cx(field, 'sm:w-44')} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as TeamRole | '')}>
            <option value="">Alle Rollen</option>
            <option value="owner">Besitzer</option>
            <option value="admin">Administratoren</option>
            <option value="member">Mitglieder</option>
          </select>
        </label>
        {data.groups.length > 0 && (
          <label className="w-full sm:w-auto">
            <span className="sr-only">Nach Gruppe filtern</span>
            <select className={cx(field, 'sm:w-52')} value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
              <option value="">Alle Gruppen</option>
              {data.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
              {admin && <option value="ohne">Ohne Gruppe</option>}
            </select>
          </label>
        )}
        <p className="w-full text-xs text-[var(--ink-soft)] sm:ml-auto sm:w-auto" aria-live="polite">
          {filtered ? `${shown.length} von ${data.members.length}` : data.members.length === 1 ? '1 Mitglied' : `${data.members.length} Mitglieder`}
        </p>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              setQ('');
              setRoleFilter('');
              setGroupFilter('');
            }}
          >
            Filter zurücksetzen
          </Button>
        )}
      </div>
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]" aria-label="Mitglieder">
        {shown.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-3 p-3">
            <Avatar user={m} size={36} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                <PersonName id={m.id} name={m.name} />
                {m.id === me?.id && <span className="font-normal text-[var(--ink-soft)]"> (du)</span>}
              </p>
              <Headline text={m.headline} />
              <p className="truncate text-xs text-[var(--ink-soft)]">
                {m.email} · seit {new Date(m.createdAt).toLocaleDateString('de-DE')}
              </p>
              {m.groupIds.length > 0 && (
                <p className="mt-1 flex flex-wrap gap-1">
                  {m.groupIds.map((gid) => {
                    const g = groupById.get(gid);
                    return g ? (
                      <span key={gid} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-xs dark:bg-white/10">
                        <span className={cx('size-2 rounded-full', GROUP_DOT[g.color])} aria-hidden /> {g.name}
                      </span>
                    ) : null;
                  })}
                </p>
              )}
            </div>
            {canEditRole(m) ? (
              <label className="text-sm">
                <span className="sr-only">Rolle von {m.name}</span>
                <select
                  className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 sm:min-h-9"
                  value={m.role}
                  onChange={(e) => void run(() => api.patch(`/api/teams/${teamId}/members/${m.id}`, { role: e.target.value }), 'Rolle geändert.')}
                >
                  {roleOptions(m).map((r) => (
                    <option key={r} value={r}>
                      {TEAM_ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className="rounded-full bg-black/5 px-2.5 py-1 text-xs font-medium dark:bg-white/10">{TEAM_ROLE_LABELS[m.role]}</span>
            )}
            {admin && data.groups.length > 0 && (
              <IconButton label={`Gruppen von ${m.name} ändern`} onClick={() => setEditGroups(m)}>
                <UsersRound size={18} />
              </IconButton>
            )}
            {m.id === me?.id ? (
              <Button
                variant="ghost"
                onClick={() => {
                  if (!window.confirm('Team verlassen? Deine persönlichen Freigaben auf Boards dieses Teams enden damit.')) return;
                  void run(async () => {
                    await api.del(`/api/teams/${teamId}/members/${m.id}`, {});
                    navigate('/teams');
                  }, 'Du hast das Team verlassen.', m);
                }}
              >
                <LogOut size={16} aria-hidden /> Verlassen
              </Button>
            ) : (
              admin &&
              (my === 'owner' || m.role === 'member') && (
                <DropdownMenu>
                  <DropdownTrigger asChild>
                    <IconButton label={`Aktionen für ${m.name}`}>
                      <MoreHorizontal size={18} />
                    </IconButton>
                  </DropdownTrigger>
                  <DropdownContent>
                    <DropdownItem
                      icon={<Trash2 size={16} />}
                      onSelect={() => {
                        if (!window.confirm(`${m.name} aus dem Team entfernen?\n\nGruppenmitgliedschaften und persönliche Freigaben auf Boards dieses Teams enden.`)) return;
                        void run(() => api.del(`/api/teams/${teamId}/members/${m.id}`, {}), `${m.name} wurde entfernt.`, m);
                      }}
                    >
                      Aus dem Team entfernen
                    </DropdownItem>
                    <DropdownItem
                      danger
                      icon={<Ban size={16} />}
                      onSelect={() => {
                        if (
                          !window.confirm(
                            `${m.name} entfernen und sperren?\n\nZusätzlich ist kein Wiederbeitritt über alte Gastlinks möglich. Aufheben lässt sich die Sperre nur durch eine ausdrückliche neue Einladung.`,
                          )
                        )
                          return;
                        void run(() => api.del(`/api/teams/${teamId}/members/${m.id}`, { block: true }), `${m.name} wurde entfernt und gesperrt.`, m);
                      }}
                    >
                      Entfernen und sperren
                    </DropdownItem>
                    {my === 'owner' && (
                      <DropdownItem danger icon={<ShieldAlert size={16} />} onSelect={() => setEmergency(m)}>
                        Notfall: sofort sperren
                      </DropdownItem>
                    )}
                  </DropdownContent>
                </DropdownMenu>
              )
            )}
          </li>
        ))}
        {shown.length === 0 && <li className="p-4 text-sm text-[var(--ink-soft)]">Niemand gefunden.</li>}
      </ul>

      {admin && data.invitations.length > 0 && (
        <section aria-label="Offene Einladungen">
          <h2 className="mb-2 text-sm font-semibold text-[var(--ink-soft)]">Offene Einladungen</h2>
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
            {data.invitations.map((i) => {
              const expired = !!i.expiresAt && new Date(i.expiresAt) < new Date();
              return (
                <li key={i.id} className="flex flex-wrap items-center gap-2 p-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{i.email}</p>
                    <p className="text-xs text-[var(--ink-soft)]">
                      {TEAM_ROLE_LABELS[i.role]}
                      {i.invitedByName ? ` · eingeladen von ${i.invitedByName}` : ''}
                      {i.expiresAt &&
                        (expired ? (
                          <span className="font-medium text-amber-700 dark:text-amber-300"> · abgelaufen</span>
                        ) : (
                          ` · gültig bis ${new Date(i.expiresAt).toLocaleDateString('de-DE')}`
                        ))}
                    </p>
                    {i.groupIds.length > 0 && (
                      <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-[var(--ink-soft)]">
                        Nach dem Beitritt in:
                        {i.groupIds.map((gid) => {
                          const g = groupById.get(gid);
                          return g ? (
                            <span key={gid} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 dark:bg-white/10">
                              <span className={cx('size-2 rounded-full', GROUP_DOT[g.color])} aria-hidden /> {g.name}
                            </span>
                          ) : null;
                        })}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => void run(() => api.post(`/api/teams/${teamId}/invitations/${i.id}/resend`), `Einladung an ${i.email} erneut gesendet.`)}
                  >
                    <RefreshCw size={15} aria-hidden /> Erneut senden
                  </Button>
                  <IconButton label={`Einladung an ${i.email} zurückziehen`} onClick={() => void run(() => api.del(`/api/teams/${teamId}/invitations/${i.id}`), 'Einladung zurückgezogen.')}>
                    <Trash2 size={16} />
                  </IconButton>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {admin && data.blocks.length > 0 && (
        <section aria-label="Gesperrt">
          <h2 className="mb-2 text-sm font-semibold text-[var(--ink-soft)]">Gesperrt</h2>
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
            {data.blocks.map((b) => (
              <li key={b.userId} className="flex flex-wrap items-center gap-3 p-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{b.name}</span>
                  <span className="block truncate text-xs text-[var(--ink-soft)]">gesperrt seit {new Date(b.createdAt).toLocaleDateString('de-DE')}</span>
                </span>
                <Button
                  onClick={() => {
                    if (!window.confirm(`Sperre für ${b.name} aufheben und erneut ins Team einladen? Alte Freigaben kommen dadurch nicht zurück.`)) return;
                    void run(() => api.post(`/api/teams/${teamId}/invitations`, { email: b.email, role: 'member', unblock: true }), 'Sperre aufgehoben.');
                  }}
                >
                  Sperre aufheben und einladen
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <InviteDialog open={inviting} onClose={() => setInviting(false)} teamId={teamId} myRole={my} groups={data.groups} />
      {editGroups && <MemberGroupsDialog member={editGroups} data={data} onClose={() => setEditGroups(null)} />}
      {blocked && (
        <Sheet open onOpenChange={(o) => !o && setBlocked(null)} title="Entfernen noch nicht möglich" side="center">
          <div className="flex flex-col gap-3 p-4 text-sm">
            <p>{blocked.message}</p>
            {blocked.boards.length > 0 && (
              <ul aria-label="Allein besessene Boards" className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
                {blocked.boards.map((b) => (
                  <li key={b.id}>
                    <a
                      href={`/b/${b.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/b/${b.id}`);
                      }}
                      className="flex min-h-11 items-center px-3 text-accent-fg underline"
                    >
                      {b.title}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[var(--ink-soft)]">Im Board unter „Mitglieder“ → „Besitz übergeben“ bestimmst du einen neuen Besitzer.</p>
            <div className="flex flex-wrap justify-end gap-2">
              {my === 'owner' && blocked.member.id !== me?.id && (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600"
                  onClick={() => {
                    setEmergency(blocked.member);
                    setBlocked(null);
                  }}
                >
                  <ShieldAlert size={16} aria-hidden /> Notfall: sofort sperren
                </Button>
              )}
              <Button variant="primary" onClick={() => setBlocked(null)}>
                Verstanden
              </Button>
            </div>
          </div>
        </Sheet>
      )}
      {emergency && <EmergencyDialog teamId={teamId} member={emergency} onClose={() => setEmergency(null)} />}
    </div>
  );
}

function ViewSwitch({ view, onChange }: { view: 'mitglieder' | 'externe'; onChange: (v: 'mitglieder' | 'externe') => void }) {
  return (
    <div role="group" aria-label="Anzeigen" className="flex self-start rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
      {(
        [
          ['mitglieder', 'Teammitglieder'],
          ['externe', 'Externe'],
        ] as const
      ).map(([k, label]) => (
        <button
          key={k}
          type="button"
          aria-pressed={view === k}
          onClick={() => onChange(k)}
          className={cx('min-h-11 rounded-md px-3 text-sm sm:min-h-10', view === k ? 'bg-[var(--panel)] font-semibold shadow-sm' : 'text-[var(--ink-soft)]')}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Externe Personen mit Zugriff auf Boards des Teams; „Teamzugriff entziehen“ (L02) */
function ExternalsList({ teamId }: { teamId: string }) {
  const { data } = useQuery({ queryKey: ['team-externals', teamId], queryFn: () => api.get<TeamExternal[]>(`/api/teams/${teamId}/externals`) });
  const revoke = async (x: TeamExternal, block: boolean) => {
    const what = block ? 'Teamzugriff entziehen und sperren' : 'Teamzugriff entziehen';
    if (
      !window.confirm(
        `${what}: ${x.name}?\n\nAlle Freigaben auf Boards dieses Teams enden, auch über Einladungslinks. Offene Zuständigkeiten werden neu zu vergeben.${block ? ' Ein erneuter Beitritt über alte Links ist nicht möglich.' : ''} Das Konto und Zugänge zu anderen Teams bleiben bestehen.`,
      )
    )
      return;
    try {
      await api.post(`/api/teams/${teamId}/externals/${x.id}/revoke`, { block });
      toast.info(`${x.name} hat keinen Zugriff mehr auf Boards dieses Teams.`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      await refreshTeam(teamId);
    }
  };
  if (!data) return <p className="text-sm text-[var(--ink-soft)]">Lade …</p>;
  return (
    <section aria-labelledby="ext-title" className="flex flex-col gap-2">
      <h2 id="ext-title" className="text-sm font-semibold">
        Externe mit Zugriff auf Boards des Teams
      </h2>
      <p className="text-sm text-[var(--ink-soft)]">
        Personen außerhalb des Teams, denen ein Board freigegeben wurde – mit eigenem Konto oder als Gast über einen Einladungslink. Sie sehen keine
        Teamverwaltung und keine Mitgliederliste des Teams.
      </p>
      {data.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-6 text-center text-sm text-[var(--ink-soft)]">Keine externen Personen.</div>
      ) : (
        <ul aria-label="Externe" className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
          {data.map((x) => (
            <li key={x.id} className="flex flex-wrap items-center gap-3 p-3">
              <Avatar user={{ ...x, image: null }} size={32} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{x.name}</p>
                <p className="truncate text-xs text-[var(--ink-soft)]">
                  <span className="mr-1 rounded bg-sky-100 px-1.5 py-px text-[11px] font-medium text-sky-900 dark:bg-sky-950 dark:text-sky-200">
                    {x.guest ? 'Linkgast' : 'Extern'}
                  </span>
                  {x.email || 'ohne Konto'} · {x.boardCount === 1 ? '1 Board' : `${x.boardCount} Boards`}
                </p>
              </div>
              <DropdownMenu>
                <DropdownTrigger asChild>
                  <IconButton label={`Aktionen für ${x.name}`}>
                    <MoreHorizontal size={18} />
                  </IconButton>
                </DropdownTrigger>
                <DropdownContent>
                  <DropdownItem icon={<Trash2 size={16} />} onSelect={() => void revoke(x, false)}>
                    Teamzugriff entziehen
                  </DropdownItem>
                  <DropdownItem danger icon={<Ban size={16} />} onSelect={() => void revoke(x, true)}>
                    Entziehen und sperren
                  </DropdownItem>
                </DropdownContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Notfallsperre (L03): mit Zusammenfassung und ausdrücklicher Bestätigung */
function EmergencyDialog({ teamId, member, onClose }: { teamId: string; member: TeamMember; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={`Notfall: ${member.name} sofort sperren`} side="center">
      <form
        className="flex flex-col gap-3 p-4 text-sm"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const r = await api.post<{ ownerless: number }>(`/api/teams/${teamId}/members/${member.id}/emergency`, { reason: reason || undefined });
            toast.info(
              r.ownerless
                ? `${member.name} ist gesperrt. ${r.ownerless === 1 ? '1 Board ist' : `${r.ownerless} Boards sind`} jetzt ohne Besitzer – unter „Verwaltung“ wiederherstellen.`
                : `${member.name} ist gesperrt.`,
            );
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
            await refreshTeam(teamId);
          }
        }}
      >
        <div className="flex gap-2 rounded-lg bg-rose-50 p-3 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
          <ul className="list-disc pl-4">
            <li>Der Zugang zu allen Boards dieses Teams endet sofort, auch über Einladungslinks und offene Verbindungen.</li>
            <li>Boards, die {member.name} allein besitzt, bleiben erhalten, sind aber schreibgeschützt, bis du unter „Verwaltung“ einen neuen Besitzer bestimmst.</li>
            <li>Offene Zuständigkeiten werden neu zu vergeben. Kommentare behalten ihren Autor.</li>
            <li>Die Sperre und alle Wiederherstellungen stehen im Protokoll.</li>
          </ul>
        </div>
        <label className="flex flex-col gap-1.5 font-medium">
          Anlass (optional, fürs Protokoll)
          <input className={field} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Konto kompromittiert" />
        </label>
        <label className="flex min-h-11 items-center gap-3">
          <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          Ich habe die Folgen gelesen und will {member.name} jetzt sperren.
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" className="bg-rose-600 hover:bg-rose-700" disabled={!confirmed || busy}>
            Sofort sperren
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

function InviteDialog({ open, onClose, teamId, myRole, groups }: { open: boolean; onClose: () => void; teamId: string; myRole: TeamRole; groups: TeamGroup[] }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('member');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  /** Einladung (Person nimmt selbst an) oder ausdrücklich „Direkt aufnehmen“ für bestehende Konten (F03) */
  const send = async (direct: boolean) => {
    setBusy(true);
    try {
      const r = await api.post<{ status: 'added' | 'invited' }>(`/api/teams/${teamId}/invitations`, { email, role, groupIds, direct });
      toast.info(r.status === 'added' ? 'Die Person ist jetzt im Team.' : 'Einladung verschickt. Die Person wird Mitglied, sobald sie annimmt.');
      setEmail('');
      setGroupIds([]);
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
      await refreshTeam(teamId);
    }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Ins Team einladen" side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          void send(false);
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          E-Mail-Adresse
          <input className={field} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Rolle im Team
          <select className={field} value={role} onChange={(e) => setRole(e.target.value as TeamRole)}>
            <option value="member">{TEAM_ROLE_LABELS.member}</option>
            <option value="admin">{TEAM_ROLE_LABELS.admin}</option>
            {myRole === 'owner' && <option value="owner">{TEAM_ROLE_LABELS.owner}</option>}
          </select>
        </label>
        {groups.length > 0 && (
          <fieldset>
            <legend className="mb-1 text-sm font-medium">Gruppen (optional)</legend>
            <p className="mb-1.5 text-xs text-[var(--ink-soft)]">Wirksam mit dem Beitritt. Die Person erhält dann Zugriff auf die Boards, die für diese Gruppen freigegeben sind.</p>
            <ul className="flex max-h-48 flex-col overflow-y-auto rounded-xl border border-[var(--line)]">
              {groups.map((g) => (
                <li key={g.id}>
                  <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 text-sm">
                    <input
                      type="checkbox"
                      className="size-5 accent-[var(--color-accent)]"
                      checked={groupIds.includes(g.id)}
                      onChange={(e) => setGroupIds((l) => (e.target.checked ? [...l, g.id] : l.filter((x) => x !== g.id)))}
                    />
                    <span className={cx('size-2.5 rounded-full', GROUP_DOT[g.color])} aria-hidden /> {g.name}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
        <p className="rounded-lg bg-black/5 p-3 text-sm text-[var(--ink-soft)] dark:bg-white/5">
          Die Teammitgliedschaft allein gibt keinen Boardzugriff. Gewählte Gruppen können beim Beitritt Zugriff auf ihre freigegebenen Boards erteilen.
          Die Person wird erst Mitglied, wenn sie die Einladung annimmt.
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" className="mr-auto" disabled={busy || !email.trim()} onClick={() => void send(true)} aria-describedby="direct-hint">
            Direkt aufnehmen
          </Button>
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !email.trim()}>
            Einladung senden
          </Button>
        </div>
        <p id="direct-hint" className="-mt-2 text-xs text-[var(--ink-soft)]">
          „Direkt aufnehmen“ geht nur bei bestehenden Konten mit bestätigter Adresse und macht die Person ohne Rückfrage zum Mitglied.
        </p>
      </form>
    </Sheet>
  );
}

/** Gruppen einer Person ändern (Mitgliederverwaltung, Paket 4) – mit konkreter Wirkung (U03) */
function MemberGroupsDialog({ member, data, onClose }: { member: TeamMember; data: TeamDetail; onClose: () => void }) {
  const [chosen, setChosen] = useState<string[]>(member.groupIds);
  const [busy, setBusy] = useState(false);
  const added = chosen.filter((g) => !member.groupIds.includes(g));
  const removed = member.groupIds.filter((g) => !chosen.includes(g));
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={`Gruppen von ${member.name}`} side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await api.put(`/api/teams/${data.team.id}/members/${member.id}/groups`, { groupIds: chosen });
            toast.info('Gruppen gespeichert.');
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
            await refreshTeam(data.team.id);
          }
        }}
      >
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">Gruppen</legend>
          <ul className="flex max-h-72 flex-col overflow-y-auto rounded-xl border border-[var(--line)]">
            {data.groups.map((g) => (
              <li key={g.id}>
                <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 text-sm hover:bg-black/[0.03] dark:hover:bg-white/5">
                  <input
                    type="checkbox"
                    className="size-5 accent-[var(--color-accent)]"
                    checked={chosen.includes(g.id)}
                    onChange={(e) => setChosen((l) => (e.target.checked ? [...l, g.id] : l.filter((x) => x !== g.id)))}
                  />
                  <span className={cx('size-2.5 rounded-full', GROUP_DOT[g.color])} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{g.name}</span>
                  <span className="text-xs text-[var(--ink-soft)]">{g.memberIds.length}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        {(added.length > 0 || removed.length > 0) && (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" role="status">
            {added.length > 0 && `${member.name} erhält dadurch Zugriff auf die Boards, die für ${added.length === 1 ? 'diese Gruppe' : 'diese Gruppen'} freigegeben sind. `}
            {removed.length > 0 && `Den Zugriff über ${removed.length === 1 ? 'die entfernte Gruppe' : 'die entfernten Gruppen'} verliert ${member.name}, sofern er nicht auf anderem Weg besteht.`}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || (!added.length && !removed.length)}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Gruppen ---------- */

function GroupsTab({ data }: { data: TeamDetail }) {
  const teamId = data.team.id;
  const admin = teamRoleAtLeast(data.team.myRole, 'admin');
  const [edit, setEdit] = useState<TeamGroup | 'new' | null>(null);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-sm text-[var(--ink-soft)]">
          Gruppen wie „Gestaltung“ oder „Projektleitung“ fassen Teammitglieder zusammen. Eine Person kann in mehreren Gruppen sein. Zugriff auf ein Board gibt
          eine Gruppe erst, wenn das Board für sie freigegeben ist.
        </p>
        {admin && (
          <Button variant="primary" onClick={() => setEdit('new')}>
            <Plus size={17} aria-hidden /> Neue Gruppe
          </Button>
        )}
      </div>
      {data.groups.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-6 text-center text-sm text-[var(--ink-soft)]">
          {admin ? 'Noch keine Gruppen.' : 'Du gehörst keiner Gruppe an.'}
        </div>
      )}
      <ul className="flex flex-col gap-2" aria-label="Gruppen">
        {data.groups.map((g) => (
          <li key={g.id} className="flex items-center gap-3 rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-3">
            <span className={cx('size-3 shrink-0 rounded-full', GROUP_DOT[g.color])} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{g.name}</p>
              <p className="truncate text-xs text-[var(--ink-soft)]">
                {g.memberIds.length === 1 ? '1 Mitglied' : `${g.memberIds.length} Mitglieder`}
                {g.description ? ` · ${g.description}` : ''}
              </p>
            </div>
            {admin && <Button onClick={() => setEdit(g)}>Bearbeiten</Button>}
          </li>
        ))}
      </ul>
      {edit && <GroupDialog key={edit === 'new' ? 'new' : edit.id} group={edit === 'new' ? null : edit} data={data} onClose={() => setEdit(null)} teamId={teamId} />}
    </div>
  );
}

function GroupDialog({ group, data, onClose, teamId }: { group: TeamGroup | null; data: TeamDetail; onClose: () => void; teamId: string }) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [color, setColor] = useState<GroupColor>(group?.color ?? 'gray');
  const [members, setMembers] = useState<string[]>(group?.memberIds ?? []);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  if (deleting && group) return <GroupDeleteDialog group={group} teamId={teamId} onCancel={() => setDeleting(false)} onDone={onClose} />;
  const added = members.filter((m) => !(group?.memberIds ?? []).includes(m)).length;
  const removed = (group?.memberIds ?? []).filter((m) => !members.includes(m)).length;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={group ? `Gruppe „${group.name}“` : 'Neue Gruppe'} side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            let id = group?.id;
            if (group) await api.patch(`/api/groups/${group.id}`, { name, description: description || null, color });
            else id = (await api.post<{ id: string }>(`/api/teams/${teamId}/groups`, { name, description: description || null, color })).id;
            await api.put(`/api/groups/${id}/members`, { userIds: members });
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
            await refreshTeam(teamId);
          }
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name
          <input className={field} required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Beschreibung (optional)
          <input className={field} maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">Farbe (nur zur Orientierung)</legend>
          <div className="flex flex-wrap gap-1.5">
            {GROUP_COLORS.map((c) => (
              <label key={c} className={cx('inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-sm', color === c ? 'border-accent ring-1 ring-accent' : 'border-[var(--line)]')}>
                <input type="radio" name="color" className="sr-only" checked={color === c} onChange={() => setColor(c)} />
                <span className={cx('size-3 rounded-full', GROUP_DOT[c])} aria-hidden /> {GROUP_COLOR_NAMES[c]}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">Mitglieder</legend>
          <ul className="flex max-h-64 flex-col overflow-y-auto rounded-xl border border-[var(--line)]">
            {data.members.map((m) => (
              <li key={m.id}>
                <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 text-sm hover:bg-black/[0.03] dark:hover:bg-white/5">
                  <input
                    type="checkbox"
                    className="size-5 accent-[var(--color-accent)]"
                    checked={members.includes(m.id)}
                    onChange={(e) => setMembers((list) => (e.target.checked ? [...list, m.id] : list.filter((x) => x !== m.id)))}
                  />
                  <Avatar user={m} size={24} /> <span className="truncate">{m.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        {group && <GroupBoardsSection groupId={group.id} />}
        {(added > 0 || removed > 0) && (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" role="status">
            {added > 0 && `${added === 1 ? 'Eine Person erhält' : `${added} Personen erhalten`} dadurch Zugriff auf die Boards, die für diese Gruppe freigegeben sind. `}
            {removed > 0 && `${removed === 1 ? 'Eine Person verliert' : `${removed} Personen verlieren`} diesen Zugriff, sofern sie ihn nicht auf anderem Weg haben.`}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {group && (
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-rose-600"
              onClick={() => setDeleting(true)}
            >
              <Trash2 size={16} aria-hidden /> Gruppe löschen
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Allgemein ---------- */

function GeneralTab({ data }: { data: TeamDetail }) {
  const teamId = data.team.id;
  const admin = teamRoleAtLeast(data.team.myRole, 'admin');
  const [name, setName] = useState(data.team.name);
  const [description, setDescription] = useState(data.team.description ?? '');
  const { data: boards } = useBoards();
  const candidates = (boards ?? []).filter((b) => b.role === 'owner' && !b.teamId);
  const [chosen, setChosen] = useState<string[]>([]);
  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="t-general" className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-4">
        <h2 id="t-general" className="mb-3 font-semibold">
          Name und Beschreibung
        </h2>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api.patch(`/api/teams/${teamId}`, { name, description: description || null });
              toast.info('Gespeichert.');
            } catch (err) {
              toast.error((err as Error).message);
            } finally {
              await refreshTeam(teamId);
            }
          }}
        >
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Name
            <input className={field} required maxLength={80} value={name} disabled={!admin} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Beschreibung
            <input className={field} maxLength={500} value={description} disabled={!admin} onChange={(e) => setDescription(e.target.value)} />
          </label>
          {admin && (
            <Button type="submit" variant="primary" className="self-start">
              Speichern
            </Button>
          )}
        </form>
      </section>

      <section aria-labelledby="t-adopt" className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-4">
        <h2 id="t-adopt" className="mb-1 font-semibold">
          Eigene Boards ins Team übernehmen
        </h2>
        <p className="mb-3 text-sm text-[var(--ink-soft)]">
          Boards ohne Team, die du besitzt. Freigaben bleiben unverändert; weitere Besitzer müssen schon zum Team gehören. Personen außerhalb des Teams
          bleiben externe Gäste.
        </p>
        {candidates.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)]">Keine Boards ohne Team, die du besitzt.</p>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const r = await api.post<{ adopted: string[] }>(`/api/teams/${teamId}/adopt`, { boardIds: chosen });
                toast.info(r.adopted.length === 1 ? '1 Board übernommen.' : `${r.adopted.length} Boards übernommen.`);
                setChosen([]);
              } catch (err) {
                toast.error((err as Error).message);
              } finally {
                await refreshTeam(teamId);
              }
            }}
          >
            <ul className="flex max-h-72 flex-col overflow-y-auto rounded-xl border border-[var(--line)]">
              {candidates.map((b) => (
                <li key={b.id}>
                  <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 text-sm">
                    <input
                      type="checkbox"
                      className="size-5 accent-[var(--color-accent)]"
                      checked={chosen.includes(b.id)}
                      onChange={(e) => setChosen((l) => (e.target.checked ? [...l, b.id] : l.filter((x) => x !== b.id)))}
                    />
                    {b.title}
                  </label>
                </li>
              ))}
            </ul>
            <Button type="submit" variant="primary" className="self-start" disabled={!chosen.length}>
              {chosen.length > 1 ? `${chosen.length} Boards übernehmen` : 'Übernehmen'}
            </Button>
          </form>
        )}
      </section>
    </div>
  );
}

/** Gruppenlöschung mit Zusammenfassung und bewusster Bestätigung (L01, U03) */
function GroupDeleteDialog({ group, teamId, onCancel, onDone }: { group: TeamGroup; teamId: string; onCancel: () => void; onDone: () => void }) {
  const { data } = useQuery({ queryKey: ['group-impact', group.id], queryFn: () => api.get<GroupImpact>(`/api/groups/${group.id}/impact`) });
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open onOpenChange={(o) => !o && onCancel()} title={`Gruppe „${group.name}“ löschen?`} side="center">
      <div className="flex flex-col gap-3 p-4 text-sm">
        {!data ? (
          <p className="text-[var(--ink-soft)]">Wirkung wird ermittelt …</p>
        ) : (
          <>
            <ul className="flex list-disc flex-col gap-1 pl-5" aria-label="Auswirkungen">
              <li>{data.members === 1 ? '1 Mitgliedschaft endet.' : `${data.members} Mitgliedschaften enden.`}</li>
              <li>
                {data.boards.length || data.hiddenBoards
                  ? 'Alle Boardfreigaben der Gruppe enden.'
                  : 'Die Gruppe ist für kein Board freigegeben.'}
              </li>
              {data.reminderRules > 0 && (
                <li>
                  {data.reminderRules === 1 ? '1 Erinnerungsregel nennt' : `${data.reminderRules} Erinnerungsregeln nennen`} die Gruppe als Empfänger. Sie wird dort als
                  „Empfängergruppe gelöscht“ markiert, andere Empfänger bleiben.
                </li>
              )}
              <li>Boards, Karten und Personen bleiben erhalten. Eine neue Gruppe mit gleichem Namen erbt nichts davon.</li>
            </ul>
            {data.boards.length > 0 && (
              <div>
                <h3 className="mb-1 font-medium">Betroffene Boards</h3>
                <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]" aria-label="Betroffene Boards">
                  {data.boards.map((b) => (
                    <li key={b.id} className="px-3 py-2">
                      <p className="font-medium">
                        {b.title} <span className="font-normal text-[var(--ink-soft)]">· {ROLE_LABELS[b.role]}</span>
                      </p>
                      <p className="text-xs text-[var(--ink-soft)]">
                        {b.losing.length ? `Verlieren den Zugriff: ${b.losing.join(', ')}` : 'Niemand verliert den Zugriff – alle haben ihn auch auf anderem Weg.'}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.hiddenBoards && <p className="text-xs text-[var(--ink-soft)]">Außerdem ist die Gruppe für Boards freigegeben, die du selbst nicht siehst.</p>}
            <label className="flex min-h-11 cursor-pointer items-center gap-3">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={ok} onChange={(e) => setOk(e.target.checked)} />
              Ich habe die Auswirkungen gelesen.
            </label>
          </>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onCancel}>Abbrechen</Button>
          <Button
            variant="danger"
            disabled={!ok || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.del(`/api/groups/${group.id}`);
                toast.info(`Gruppe „${group.name}“ gelöscht.`);
                onDone();
              } catch (err) {
                toast.error((err as Error).message);
              } finally {
                setBusy(false);
                await refreshTeam(teamId);
              }
            }}
          >
            <Trash2 size={16} aria-hidden /> Gruppe löschen
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

function GroupBoardsSection({ groupId }: { groupId: string }) {
  const { data } = useQuery({ queryKey: ['group-boards', groupId], queryFn: () => api.get<GroupBoards>(`/api/groups/${groupId}/boards`) });
  return (
    <section aria-labelledby={`gb-${groupId}`} className="flex flex-col gap-1.5">
      <h3 id={`gb-${groupId}`} className="text-sm font-medium">
        Boardfreigaben
      </h3>
      {!data ? (
        <p className="text-sm text-[var(--ink-soft)]">Lade …</p>
      ) : (
        <>
          {data.boards.length > 0 ? (
            <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)] text-sm">
              {data.boards.map((b) => (
                <li key={b.id} className="flex min-h-10 items-center gap-2 px-3">
                  <span className="min-w-0 flex-1 truncate">{b.title}</span>
                  <span className="text-xs text-[var(--ink-soft)]">{ROLE_LABELS[b.role]}</span>
                </li>
              ))}
            </ul>
          ) : (
            !data.hidden && <p className="text-sm text-[var(--ink-soft)]">Noch für kein Board freigegeben. Freigaben setzen die Besitzer im jeweiligen Board.</p>
          )}
          {data.hidden && <p className="text-xs text-[var(--ink-soft)]">Außerdem ist die Gruppe für Boards freigegeben, die du selbst nicht siehst.</p>}
        </>
      )}
    </section>
  );
}

/* ---------- Verwaltung (Besitzer) ---------- */

function ManagementTab({ data }: { data: TeamDetail }) {
  const teamId = data.team.id;
  const { data: boards } = useQuery({ queryKey: ['team-ownerless', teamId], queryFn: () => api.get<OwnerlessBoard[]>(`/api/teams/${teamId}/ownerless`) });
  const [pick, setPick] = useState<Record<string, string>>({});
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--ink-soft)]">
        Als Teambesitzer kannst du Boards wiederherstellen, deren Besitzer im Notfall gesperrt wurde. Du siehst hier nur den Titel – Inhalte erst, wenn du
        dich selbst als Besitzer einträgst. Jede Wiederherstellung steht im Protokoll.
      </p>
      <UnmanagedAreas teamId={teamId} />
      <section aria-labelledby="ownerless-title" className="flex flex-col gap-2">
        <h2 id="ownerless-title" className="text-sm font-semibold">
          Boards ohne aktiven Besitzer
        </h2>
        {!boards ? (
          <p className="text-sm text-[var(--ink-soft)]">Lade …</p>
        ) : boards.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-6 text-center text-sm text-[var(--ink-soft)]">Alle Boards haben einen Besitzer.</div>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
            {boards.map((b) => (
              <li key={b.id} className="flex flex-col gap-2 p-3">
                <div>
                  <p className="font-medium">{b.title}</p>
                  <p className="text-xs text-[var(--ink-soft)]">
                    schreibgeschützt seit {new Date(b.since).toLocaleDateString('de-DE')}
                    {b.formerOwner ? ` · zuvor ${b.formerOwner}` : ''}
                  </p>
                </div>
                <form
                  className="flex flex-wrap gap-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const target = data.members.find((m) => m.id === pick[b.id]);
                    if (!target || !window.confirm(`${target.name} wird Besitzer von „${b.title}“ und erhält damit Zugriff auf alle Inhalte. Fortfahren?`)) return;
                    try {
                      await api.post(`/api/teams/${teamId}/ownerless/${b.id}/recover`, { userId: target.id });
                      toast.info(`${target.name} ist jetzt Besitzer von „${b.title}“.`);
                    } catch (err) {
                      toast.error((err as Error).message);
                    } finally {
                      await refreshTeam(teamId);
                    }
                  }}
                >
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">Neuer Besitzer für {b.title}</span>
                    <select className={field} value={pick[b.id] ?? ''} onChange={(e) => setPick((p) => ({ ...p, [b.id]: e.target.value }))} required>
                      <option value="">Neuen Besitzer wählen …</option>
                      {data.members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button type="submit" variant="primary" disabled={!pick[b.id]}>
                    Besitzer bestimmen
                  </Button>
                </form>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Bereiche ohne Verwalter (z. B. nach Austritten): Teambesitzer übernehmen die Verwaltung der Struktur */
function UnmanagedAreas({ teamId }: { teamId: string }) {
  const { data } = useQuery({ queryKey: ['team-unmanaged', teamId], queryFn: () => api.get<{ id: string; name: string }[]>(`/api/teams/${teamId}/areas/unmanaged`) });
  if (!data?.length) return null;
  return (
    <section aria-labelledby="unmanaged-title" className="flex flex-col gap-2">
      <h2 id="unmanaged-title" className="text-sm font-semibold">
        Bereiche ohne Verwalter
      </h2>
      <p className="text-sm text-[var(--ink-soft)]">Übernimmst du die Verwaltung, siehst du die Struktur – Boards darin aber nur mit eigener Freigabe.</p>
      <ul className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
        {data.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-3 p-3">
            <span className="min-w-0 flex-1 font-medium">{a.name}</span>
            <Button
              onClick={async () => {
                try {
                  await api.post(`/api/areas/${a.id}/claim`);
                  toast.info(`Du verwaltest jetzt „${a.name}“.`);
                  refreshStructure();
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  await refreshTeam(teamId);
                  await queryClient.invalidateQueries({ queryKey: ['team-unmanaged', teamId] });
                }
              }}
            >
              Verwaltung übernehmen
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ---------- Protokoll ---------- */

const PROTOCOL_RANGES: [number, string][] = [
  [0, 'Gesamter Zeitraum'],
  [7, 'Letzte 7 Tage'],
  [30, 'Letzte 30 Tage'],
  [90, 'Letzte 90 Tage'],
];

const when = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

/** Geladene Einträge als CSV (Semikolon, für Tabellenprogramme) */
function protocolCsv(entries: ProtocolEntry[]): string {
  const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const head = ['Zeitpunkt', 'Bereich', 'Person', 'Aktion', 'Einzelheiten'];
  const rows = entries.map((e) => [when(e.createdAt), PROTOCOL_CATEGORY_LABELS[e.category], e.actorName ?? 'Jemand', e.summary, e.details.join(' · ')]);
  return '\ufeff' + [head, ...rows].map((r) => r.map(cell).join(';')).join('\r\n');
}

/** Teamprotokoll mit Filtern, Seiten und Export (Paket 4) */
function ProtocolTab({ teamId, data: team }: { teamId: string; data: TeamDetail }) {
  const [filter, setFilter] = useState<ProtocolFilter>({ cat: '', person: '', days: 0 });
  const q = useTeamProtocol(teamId, filter, true);
  const entries = useMemo(() => q.data?.pages.flatMap((p) => p.entries) ?? [], [q.data]);
  const set = (patch: Partial<ProtocolFilter>) => setFilter((f) => ({ ...f, ...patch }));
  const filtered = !!(filter.cat || filter.person || filter.days);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-[var(--ink-soft)]">
        Wer hat wann was in der Teamverwaltung geändert. Bereichs- und Ordnernamen stehen hier nicht, Boardnamen nur, wenn du das Board selbst siehst.
      </p>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Protokoll filtern">
        <label className="w-full sm:w-auto">
          <span className="sr-only">Bereich</span>
          <select className={cx(field, 'sm:w-56')} value={filter.cat} onChange={(e) => set({ cat: e.target.value as ProtocolCategory | '' })}>
            <option value="">Alle Bereiche</option>
            {PROTOCOL_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {PROTOCOL_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
        <label className="w-full sm:w-auto">
          <span className="sr-only">Person</span>
          <select className={cx(field, 'sm:w-48')} value={filter.person} onChange={(e) => set({ person: e.target.value })}>
            <option value="">Alle Personen</option>
            {team.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="w-full sm:w-auto">
          <span className="sr-only">Zeitraum</span>
          <select className={cx(field, 'sm:w-44')} value={filter.days} onChange={(e) => set({ days: Number(e.target.value) })}>
            {PROTOCOL_RANGES.map(([d, label]) => (
              <option key={d} value={d}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {filtered && (
          <Button variant="ghost" onClick={() => setFilter({ cat: '', person: '', days: 0 })}>
            Filter zurücksetzen
          </Button>
        )}
        <Button
          variant="ghost"
          className="sm:ml-auto"
          disabled={!entries.length}
          onClick={() => {
            const url = URL.createObjectURL(new Blob([protocolCsv(entries)], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `teamprotokoll-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          <Download size={16} aria-hidden /> Als CSV
        </Button>
      </div>
      {q.isLoading ? (
        <p className="text-sm text-[var(--ink-soft)]">Lade …</p>
      ) : !entries.length ? (
        <p className="rounded-2xl border-2 border-dashed border-[var(--line)] p-6 text-center text-sm text-[var(--ink-soft)]">
          {filtered ? 'Keine Einträge für diese Auswahl.' : 'Noch keine Einträge.'}
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]" aria-label="Protokoll">
          {entries.map((e) => (
            <li key={e.id} className={cx('flex gap-2 p-3 text-sm', e.important && 'border-l-4 border-l-amber-500')}>
              {e.important && <ShieldAlert size={16} className="mt-0.5 shrink-0 text-amber-600" aria-label="Wichtig" />}
              <div className="min-w-0 flex-1">
                <p>
                  <b>{e.actorName ?? 'Jemand'}</b> {e.summary}
                </p>
                {e.details.length > 0 && (
                  <ul className="mt-0.5 text-xs text-[var(--ink)]">
                    {e.details.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
                <p className="mt-0.5 text-xs text-[var(--ink-soft)]">
                  {when(e.createdAt)} · {PROTOCOL_CATEGORY_LABELS[e.category]}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
      {q.hasNextPage && (
        <Button className="self-center" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
          {q.isFetchingNextPage ? 'Lade …' : 'Ältere Einträge laden'}
        </Button>
      )}
    </div>
  );
}

/* ---------- Gemeinsame Übersichten des Teams ---------- */

function TeamDashboards({ teamId, canCreate }: { teamId: string; canCreate: boolean }) {
  const { data } = useDashboards();
  const mine = (data?.dashboards ?? []).filter((d) => d.scope === 'team' && d.teamId === teamId);
  const open = (id: string) => navigate(`/?ort=uebersicht&d=${id}`);
  if (!mine.length && !canCreate) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 text-sm">
      <LayoutDashboard size={17} className="text-[var(--ink-soft)]" aria-hidden />
      <span className="font-medium">Teamübersicht:</span>
      {mine.map((d) => (
        <a
          key={d.id}
          href={`/?ort=uebersicht&d=${d.id}`}
          onClick={(e) => {
            e.preventDefault();
            open(d.id);
          }}
          className="inline-flex min-h-11 items-center rounded-lg px-2 font-medium text-accent-fg underline-offset-2 hover:underline sm:min-h-9"
        >
          {d.title}
        </a>
      ))}
      {!mine.length && <span className="text-[var(--ink-soft)]">noch keine</span>}
      {canCreate && (
        <Button
          size="sm"
          className="ml-auto"
          onClick={async () => {
            try {
              const d = await api.post<{ id: string }>('/api/dashboards', { template: 'team', teamId });
              await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
              open(d.id);
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
        >
          <Plus size={15} aria-hidden /> Teamübersicht anlegen
        </Button>
      )}
    </div>
  );
}
